import { irange, mulberry32, pick } from "../sim/rng";
import { Tile, type Enemy, type EnemyKind, type HollowRun, type Vec, idx, inBounds } from "../sim/types";

interface Room {
  x: number;
  y: number;
  w: number;
  h: number;
  role: "start" | "hall" | "cache" | "landmark" | "down" | "fight";
}

const LANDMARKS = [
  { id: "pump", title: "A drowned pump house. The gauge is still ticking." },
  { id: "chapel", title: "Brick that used to be a basement chapel. The pews face the wrong way." },
  { id: "camp", title: "A Survey tarp. Coffee gone to mold. A plate stamped 07." },
  { id: "vein", title: "The wall is growing a second wall." },
  { id: "names", title: "Names scratched at knee height. Some are still being written." },
  { id: "switch", title: "A breaker box labeled HOUSE. The copper is warm." },
  { id: "chair", title: "A kitchen chair bolted to the floor, facing a blank wall." },
  { id: "well2", title: "Another well mouth, bricked from below. Something tapped once." },
];

function carveRoom(tiles: Uint8Array, W: number, H: number, r: Room, floor: number): void {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (inBounds(x, y, W, H)) tiles[idx(x, y, W)] = floor;
    }
  }
}

function carveHall(tiles: Uint8Array, W: number, H: number, ax: number, ay: number, bx: number, by: number, floor: number): void {
  let x = ax;
  let y = ay;
  while (x !== bx) {
    if (inBounds(x, y, W, H)) tiles[idx(x, y, W)] = floor;
    if (inBounds(x, y - 1, W, H)) tiles[idx(x, y - 1, W)] = floor;
    x += Math.sign(bx - x);
  }
  while (y !== by) {
    if (inBounds(x, y, W, H)) tiles[idx(x, y, W)] = floor;
    if (inBounds(x + 1, y, W, H)) tiles[idx(x + 1, y, W)] = floor;
    y += Math.sign(by - y);
  }
}

function center(r: Room): Vec {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

function walkable(tiles: Uint8Array, W: number, H: number, x: number, y: number): boolean {
  if (!inBounds(x, y, W, H)) return false;
  const t = tiles[idx(x, y, W)];
  return t === Tile.Hollow || t === Tile.Vein || t === Tile.Exit || t === Tile.Well;
}

export function generateHollow(seed: number, depth: number): HollowRun {
  const rng = mulberry32(seed);
  const W = 36 + Math.min(16, depth * 2);
  const H = 28 + Math.min(12, depth * 2);
  const tiles = new Uint8Array(W * H);
  tiles.fill(Tile.Wall);

  const rooms: Room[] = [];
  const count = 6 + Math.min(6, depth);
  for (let i = 0; i < 80 && rooms.length < count; i++) {
    const w = irange(rng, 5, 9);
    const h = irange(rng, 4, 7);
    const x = irange(rng, 2, W - w - 3);
    const y = irange(rng, 2, H - h - 3);
    const next: Room = { x, y, w, h, role: "hall" };
    if (rooms.some((r) => !(next.x + next.w + 1 < r.x || r.x + r.w + 1 < next.x || next.y + next.h + 1 < r.y || r.y + r.h + 1 < next.y))) {
      continue;
    }
    rooms.push(next);
  }
  if (rooms.length < 3) {
    rooms.push({ x: 4, y: 4, w: 8, h: 6, role: "start" });
    rooms.push({ x: 18, y: 10, w: 8, h: 6, role: "fight" });
    rooms.push({ x: 10, y: 16, w: 7, h: 5, role: "down" });
  }

  rooms[0]!.role = "start";
  rooms[rooms.length - 1]!.role = "down";
  if (rooms.length > 2) rooms[1]!.role = "landmark";
  if (rooms.length > 3) rooms[2]!.role = "fight";
  if (rooms.length > 4) rooms[3]!.role = "cache";

  const floor = Tile.Hollow;
  for (const r of rooms) carveRoom(tiles, W, H, r, floor);
  for (let i = 1; i < rooms.length; i++) {
    const a = center(rooms[i - 1]!);
    const b = center(rooms[i]!);
    carveHall(tiles, W, H, a.x | 0, a.y | 0, b.x | 0, b.y | 0, floor);
  }
  if (rooms.length > 3) {
    const a = center(rooms[0]!);
    const b = center(rooms[rooms.length - 1]!);
    carveHall(tiles, W, H, a.x | 0, a.y | 0, b.x | 0, b.y | 0, floor);
  }

  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      if (tiles[idx(x, y, W)] === Tile.Hollow && rng() < 0.04 + depth * 0.01) {
        tiles[idx(x, y, W)] = Tile.Vein;
      }
    }
  }

  const start = center(rooms[0]!);
  const down = center(rooms[rooms.length - 1]!);
  tiles[idx(down.x | 0, down.y | 0, W)] = Tile.Exit;
  tiles[idx(start.x | 0, start.y | 0, W)] = Tile.Well;

  const kinds: EnemyKind[] = depth < 2 ? ["lurcher", "coward", "skitter"] : ["lurcher", "listener", "skitter", "watcher", "vein", "coward"];
  const enemies: Enemy[] = [];
  let eid = 1;
  for (const r of rooms) {
    if (r.role === "start") continue;
    const n = r.role === "fight" ? 2 + (depth > 2 ? 1 : 0) : rng() < 0.7 ? 1 : 0;
    for (let i = 0; i < n; i++) {
      const kind = pick(rng, kinds);
      const hp = kind === "vein" ? 40 : kind === "watcher" ? 28 : 22;
      enemies.push({
        id: eid++,
        kind,
        x: r.x + 1.5 + rng() * (r.w - 3),
        y: r.y + 1.5 + rng() * (r.h - 3),
        vx: 0,
        vy: 0,
        hp,
        hpMax: hp,
        state: "idle",
        facing: { x: 0, y: 1 },
        alert: 0,
        wind: 0,
        stun: 0,
        radius: 0.38,
      });
    }
  }

  const loot: HollowRun["loot"] = [];
  const cache = rooms.find((r) => r.role === "cache") ?? rooms[1]!;
  loot.push({ x: cache.x + 2, y: cache.y + 2, item: { id: "cinder", n: 1 + (depth > 1 ? 1 : 0) }, taken: false });
  loot.push({ x: cache.x + 3, y: cache.y + 2, item: { id: depth > 1 ? "artifact" : "scrap", n: 1 }, taken: false });
  if (rng() < 0.6) loot.push({ x: cache.x + 2, y: cache.y + 3, item: { id: "lantern_oil", n: 1 }, taken: false });

  const lm = rooms.find((r) => r.role === "landmark") ?? rooms[0]!;
  const spec = LANDMARKS[depth % LANDMARKS.length]!;
  const landmarks = [{ x: lm.x + 2, y: lm.y + 2, id: spec.id, title: spec.title, read: false }];

  return {
    seed,
    depth,
    w: W,
    h: H,
    tiles,
    start,
    down,
    enemies,
    loot,
    landmarks,
    enteredAt: 0,
    maxDepthThisRun: depth,
  };
}

export function hollowReachable(run: HollowRun): boolean {
  const W = run.w;
  const H = run.h;
  const sx = run.start.x | 0;
  const sy = run.start.y | 0;
  const seen = new Uint8Array(W * H);
  const q: number[] = [sy * W + sx];
  seen[sy * W + sx] = 1;
  let i = 0;
  let found = false;
  const dx = [1, -1, 0, 0];
  const dy = [0, 0, 1, -1];
  while (i < q.length) {
    const p = q[i++]!;
    const x = p % W;
    const y = (p / W) | 0;
    if (run.tiles[p] === Tile.Exit) found = true;
    for (let k = 0; k < 4; k++) {
      const nx = x + dx[k]!;
      const ny = y + dy[k]!;
      if (!walkable(run.tiles, W, H, nx, ny)) continue;
      const ni = ny * W + nx;
      if (seen[ni]) continue;
      seen[ni] = 1;
      q.push(ni);
    }
  }
  return found;
}
